/**
 * Custom hook to manage search TextField elements.
 *
 * Why was this hook created?
 * - Backyard UI's TextField.Root does not accept custom components as direct children
 * - It expects only TextField.Input and TextField.Slot as immediate children
 * - Attempting to create a wrapper component would break the expected TextField structure
 * - This hook returns ready-to-use JSX elements that can be rendered directly
 * - Allows reuse of search TextField logic across multiple searchbars
 *
 * @returns {Object} Object containing slots and input for rendering
 * - leadingSlot: Dynamic slot that switches between back button and search icon
 * - searchInput: Main input field
 * - trailingSlot: Slot with clear button (when there is a query)
 */

import {
  CloseButton,
  Hidden,
  IconButton,
  Stack,
  TextField,
} from '@backyard-ui/core';
import { ScanBarcodeOutline } from '@backyard-ui/icons';
import { useMediaQuery } from '@backyard-ui/hooks';
import { type Dispatch, type RefObject, type SetStateAction } from 'react';
import type {
  AutocompleteApi,
  AutocompleteState,
} from '@algolia/autocomplete-core';

import type { AutocompleteItem } from '../Searchbar.types';
import { ArrowLeftIcon, SearchIcon } from '../icons';
import { searchTextFieldStyles } from './SearchTextField.styles';
import { getAutocompleteProps } from '../Searchbar.utilts';

export interface UseSearchTextFieldProps {
  autocomplete: AutocompleteApi<
    AutocompleteItem,
    React.BaseSyntheticEvent,
    React.MouseEvent,
    React.KeyboardEvent
  >;
  autocompleteState: AutocompleteState<AutocompleteItem>;
  setAutocompleteState: Dispatch<
    SetStateAction<AutocompleteState<AutocompleteItem>>
  >;
  inputRef: RefObject<HTMLInputElement>;
  isAssistedSale?: boolean;
  onBarcodeClick?: () => void;
}

export function useSearchTextField(props: UseSearchTextFieldProps) {
  const {
    autocomplete,
    autocompleteState,
    setAutocompleteState,
    inputRef,
    isAssistedSale,
    onBarcodeClick,
  } = props;

  const isDesktop = useMediaQuery('lg');

  const inputFontSize = isDesktop ? '16px' : '12px';

  const AUTOCOMPLETE_ID = '@autocomplete-quicksearch-input';

  const autocompleteProps = getAutocompleteProps(
    autocomplete,
    inputRef,
    AUTOCOMPLETE_ID,
  );

  const backButtonSlot = (
    <TextField.Slot UNSAFE_className="px-1 lg:pl-4">
      <IconButton
        type="button"
        ariaLabel="Fechar busca"
        size="xs"
        appearance="neutral"
        variant="ghost"
        icon={<ArrowLeftIcon />}
        onClick={() => {
          setAutocompleteState((prev) => ({
            ...prev,
            isOpen: false,
          }));
        }}
      />
    </TextField.Slot>
  );

  const searchIconSlot = (
    <TextField.Slot UNSAFE_className="px-1 lg:pl-4">
      <IconButton
        appearance="neutral"
        variant="ghost"
        size="xs"
        ariaLabel="Buscar"
        type="submit"
        icon={<SearchIcon />}
        UNSAFE_className="px-1 [&_svg]:w-4 [&_svg]:h-4"
      />
    </TextField.Slot>
  );

  const leadingSlot =
    !isDesktop && autocompleteState.isOpen ? backButtonSlot : searchIconSlot;

  const searchInput = (
    <TextField.Input
      UNSAFE_className={searchTextFieldStyles()}
      UNSAFE_style={{ fontSize: inputFontSize }}
      ref={inputRef}
      {...autocompleteProps.input}
    />
  );

  const trailingSlot = (
    <TextField.Slot>
      <Stack align="center" spacing={1}>
        {autocompleteState.query && (
          <CloseButton
            type="reset"
            ariaLabel="Limpar campo de busca"
            size="xs"
            onClick={() => {
              autocomplete.setQuery('');
              inputRef.current?.focus();
            }}
          />
        )}

        {isAssistedSale && (
          <Hidden above="md">
            <IconButton
              appearance="neutral"
              variant="ghost"
              size="xs"
              ariaLabel="Escanear código de barras"
              type="button"
              icon={<ScanBarcodeOutline />}
              UNSAFE_className="px-1 [&_svg]:w-4 [&_svg]:h-4"
              onClick={onBarcodeClick}
            />
          </Hidden>
        )}
      </Stack>
    </TextField.Slot>
  );

  return {
    leadingSlot,
    searchIconSlot,
    searchInput,
    trailingSlot,
  };
}
